import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Save, Upload, Link2Off, Crop } from "lucide-react";
import { toast } from "sonner";
import { Loading } from "@/components/Loading";
import { downscaleImage } from "@/lib/eventLib";
import PhotoCropDialog from "@/components/idc/PhotoCropDialog";
import BulkPhotoUpload from "@/components/idc/BulkPhotoUpload";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicDataEntry({ token }) {
  const [meta, setMeta] = useState(null);
  const [rows, setRows] = useState([]);
  const [photoCache, setPhotoCache] = useState({});
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [cropState, setCropState] = useState(null);
  const photoInputs = useRef({});

  useEffect(() => {
    axios.get(`${API}/public/dataset/${token}`).then(async (r) => {
      setMeta({ name: r.data.name, fields: r.data.fields || [] });
      setRows(r.data.rows || []);
      const ids = [];
      (r.data.fields || []).filter((f) => f.type === "photo").forEach((f) =>
        (r.data.rows || []).forEach((row) => { if (row[f.key]) ids.push(row[f.key]); })
      );
      if (ids.length) {
        try {
          const { data } = await axios.post(`${API}/public/dataset/${token}/photos/batch`, { ids });
          setPhotoCache(data);
        } catch { /* ignore */ }
      }
    }).catch((e) => setError(e.response?.data?.detail || "Ye link active nahi hai. Owner se naya link lein."));
  }, [token]);

  const addRow = () => setRows((rs) => [...rs, {}]);
  const updateCell = (ri, key, value) => setRows((rs) => rs.map((r, i) => (i === ri ? { ...r, [key]: value } : r)));

  const onPhoto = async (ri, key, file) => {
    if (!file) return;
    try {
      // Badi photos auto-compress karke crop dialog me kholte hain — upload hamesha chalta hai
      const src = await downscaleImage(file, 1600, 0.85);
      setCropState({ ri, key, src });
    } catch {
      toast.error("Photo load nahi hui");
    }
  };

  const onCropDone = async (dataUrl) => {
    const { ri, key } = cropState;
    setCropState(null);
    try {
      const { data } = await axios.post(`${API}/public/dataset/${token}/photos`, { data: dataUrl });
      setPhotoCache((c) => ({ ...c, [data.id]: dataUrl }));
      updateCell(ri, key, data.id);
      toast.success("Photo added — Save karna na bhoolein");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Photo upload failed");
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/public/dataset/${token}/rows`, { rows });
      toast.success("Data saved successfully");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6" data-testid="share-invalid">
        <div className="bg-card border border-border rounded-xl shadow-sm p-8 text-center max-w-sm">
          <Link2Off className="h-10 w-10 mx-auto text-red-400 mb-3" />
          <h1 className="font-heading text-xl font-bold">Link Not Active</h1>
          <p className="text-sm text-muted-foreground mt-2">{error}</p>
        </div>
      </div>
    );
  }

  if (!meta) {
    return <div className="min-h-screen flex items-center justify-center bg-background"><Loading text="Loading data form..." /></div>;
  }

  const fields = meta.fields;

  return (
    <div className="min-h-screen bg-background" data-testid="share-entry-page">
      <header className="sticky top-0 z-20 bg-white border-b border-border">
        <div className="h-1 flex">
          <div className="flex-1 bg-cyan-500" />
          <div className="flex-1 bg-pink-600" />
          <div className="flex-1 bg-yellow-400" />
          <div className="flex-1 bg-slate-900" />
        </div>
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3">
          <img src="/logo.webp?v=2" alt="Sootrik" className="h-8 w-auto" />
          <div className="flex-1 min-w-0">
            <p className="font-heading font-bold truncate">{meta.name || "Data Entry"}</p>
            <p className="text-xs text-muted-foreground">Shared data entry — sirf data bharein aur Save karein</p>
          </div>
          <Button onClick={save} disabled={saving} data-testid="share-save-btn" className="gap-1.5 bg-cyan-600 hover:bg-cyan-700 text-white">
            <Save className="h-4 w-4" /> {saving ? "Saving..." : "Save"}
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6 space-y-4">
        {fields.length > 0 && (
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <div className="px-4 py-2.5 border-b border-border flex items-center justify-between">
              <p className="font-heading font-semibold text-sm">Data ({rows.length} rows)</p>
              <div className="flex items-center gap-2">
                <BulkPhotoUpload
                  photoFields={fields.filter((f) => f.type === "photo")}
                  rowCount={rows.length}
                  onUpload={async (dataUrl) => (await axios.post(`${API}/public/dataset/${token}/photos`, { data: dataUrl })).data.id}
                  onAssign={(ri, key, pid, dataUrl) => {
                    setRows((rs) => rs.map((r, i) => (i === ri ? { ...r, [key]: pid } : r)));
                    setPhotoCache((c) => ({ ...c, [pid]: dataUrl }));
                  }}
                  toastSuffix=" — Save karna na bhoolein"
                  size="sm"
                  testId="share-bulk-photo"
                />
                <Button size="sm" variant="outline" onClick={addRow} data-testid="share-add-row-btn" className="gap-1"><Plus className="h-3.5 w-3.5" /> Add Row</Button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="share-data-table">
                <thead>
                  <tr className="bg-slate-50 text-left">
                    <th className="px-2 py-2 text-xs text-slate-400 w-8">#</th>
                    {fields.map((f) => (
                      <th key={f.key} className="px-2 py-2 text-xs font-semibold text-slate-500 whitespace-nowrap">{f.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, ri) => (
                    <tr key={ri} className="border-t border-border" data-testid={`share-row-${ri}`}>
                      <td className="px-2 py-1.5 text-xs text-slate-400">{ri + 1}</td>
                      {fields.map((f) => (
                        <td key={f.key} className="px-2 py-1.5">
                          {f.type === "photo" ? (
                            <div className="flex items-center gap-1.5">
                              {row[f.key] && photoCache[row[f.key]] && (
                                <img src={photoCache[row[f.key]]} alt="" className="h-8 w-8 rounded object-cover border cursor-pointer" title="Click to adjust photo" onClick={() => setCropState({ ri, key: f.key, src: photoCache[row[f.key]] })} data-testid={`share-photo-thumb-${ri}`} />
                              )}
                              <input type="file" accept="image/*" className="hidden" ref={(el) => (photoInputs.current[`${ri}-${f.key}`] = el)} onChange={(e) => { onPhoto(ri, f.key, e.target.files[0]); e.target.value = ""; }} />
                              {row[f.key] && photoCache[row[f.key]] && (
                                <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => setCropState({ ri, key: f.key, src: photoCache[row[f.key]] })} data-testid={`share-photo-adjust-${ri}`}>
                                  <Crop className="h-3 w-3" /> Adjust
                                </Button>
                              )}
                              <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => photoInputs.current[`${ri}-${f.key}`]?.click()} data-testid={`share-photo-upload-${ri}`}>
                                <Upload className="h-3 w-3" /> {row[f.key] ? "Change" : "Upload"}
                              </Button>
                            </div>
                          ) : (
                            <Input
                              type={f.type === "number" ? "number" : f.type === "email" ? "email" : f.type === "date" ? "date" : f.type === "time" ? "time" : "text"}
                              value={row[f.key] || ""}
                              onChange={(e) => updateCell(ri, f.key, e.target.value)}
                              className="h-7 text-xs min-w-28"
                              data-testid={`share-cell-${ri}-${f.key}`}
                            />
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                  {rows.length === 0 && (
                    <tr><td colSpan={fields.length + 1} className="px-4 py-6 text-center text-muted-foreground text-sm">"Add Row" se nayi entry shuru karein.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
        <p className="text-xs text-muted-foreground text-center">Changes tabhi save honge jab aap upar Save button dabayenge.</p>
      </main>

      <PhotoCropDialog
        open={!!cropState}
        src={cropState?.src}
        onClose={() => setCropState(null)}
        onDone={onCropDone}
      />
    </div>
  );
}
